import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import beneficiarioService from '../../services/beneficiarioService';
import { useGlobalState } from '../../state';
import { FaFileAlt, FaSearch } from 'react-icons/fa';
import { toCurrency } from '../../utils/formaters';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';

type Recibo = {
  id: string;
  periodo: string;
  descripcion: string;
  total: number;
  disponible: boolean;
  numero: string;
};

const mesesNombre = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];

const ReciboDeHaberes: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useGlobalState('user');

  const [search, setSearch] = useState<string>('');
  const [recibos, setRecibos] = useState<Recibo[]>([]);
  const [filtered, setFiltered] = useState<Recibo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(undefined);
      try {
        if (!user) {
          // user not loaded yet
          setLoading(false);
          return;
        }

        const fechaHasta = new Date();
        const fechaDesde = new Date();
        fechaDesde.setMonth(fechaDesde.getMonth() - 48);

        const formatDate = (d: Date) => d.toISOString().split('T')[0];
        
        const filtro = {
          TipoLiquidacion: 1,
          TipoBeneficiario: user.datosUsuario.beneficiario?.tipoBeneficiarioId,
          IdBeneficiario: parseInt(user.datosUsuario.legajoPrevisional, 10),
          Periodo: undefined,
          FechaDesde: formatDate(fechaDesde),
          FechaHasta: formatDate(fechaHasta),
        };

        const response: any = await beneficiarioService.getRecibosAfiliado(
          filtro.IdBeneficiario,
          filtro.TipoBeneficiario,
          filtro.Periodo,
          filtro.TipoLiquidacion,
          filtro.FechaDesde,
          filtro.FechaHasta
        );

        const rows = response?.data && Array.isArray(response.data) ? response.data : [];

        const data: Recibo[] = rows.map((item: any, index: number) => {
          const periodoStr = String(item.periodo ?? '');
          const mesNum = periodoStr.slice(4, 6);
          const mesLetra = mesesNombre[parseInt(mesNum, 10) - 1] ?? mesNum;
          return {
            id: String(index),
            periodo: periodoStr,
            descripcion: `${mesLetra} ${periodoStr.slice(0, 4)}`,
            total: Number(item.totalNeto ?? item.total ?? 0),
            disponible: true,
            numero: String(item.numero ?? ''),
          };
        });

        setRecibos(data);
        setFiltered(data);
      } catch (ex: any) {
        console.error('Error al cargar recibos:', ex);
        setError(ex?.message ?? 'Ocurrió un error al cargar recibos');
        setRecibos([]);
        setFiltered([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSearch = (value: string) => {
    setSearch(value);
    const text = value.trim();
    if (text === '') {
      setFiltered(recibos);
      return;
    }
    const lower = text.toLowerCase();
    const result = recibos.filter(
      r => r.descripcion.toLowerCase().includes(lower) || r.periodo.includes(lower)
    );
    setFiltered(result);
  };

  const verRecibo = (recibo: Recibo) => {
    const anio = recibo.periodo.slice(0, 4);
    const mes = recibo.periodo.slice(4, 6);
    // navigate to detalle page (query params)
    navigate(`/recibos/detalle?mes=${encodeURIComponent(mes)}&anio=${encodeURIComponent(anio)}&numero=${encodeURIComponent(recibo.numero)}`);
  };

  return (
      <DefaultLayout>
          <Breadcrumb pageName="Recibos de haberes" />

        <div className="p-6">              
              <div className="max-w-6xl mx-auto">

                  {error && <Alert type="failure" message={error} />}

       {/*   <h1 className="text-2xl font-semibold mb-6">Recibos de haberes</h1>*/}
          <p className="text-sm text-gray-600 mb-4">Acá puedes consultar y descargar tus recibos de haberes de los últimos meses</p>

          {loading && <Loading statusText="Cargando recibos..." />}
         
          {!loading && !error && (
            <>
              <div className="mb-4">
                <label htmlFor="search" className="sr-only">Buscar recibos</label>
                <div className="relative">
                  <input
                    id="search"
                    type="search"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Buscar por mes o año..."
                    className="w-full rounded-md border border-gray-300 p-2 pl-10 focus:outline-none dark:bg-boxdark"
                    aria-label="Buscar recibos"
                  />
                  <div className="absolute left-3 top-2.5 text-gray-400">
                    <FaSearch />
                  </div>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="bg-white dark:bg-boxdark rounded p-6 text-center text-gray-600">No se encontraron recibos</div>
              ) : (
                <div className="space-y-3">
                  {filtered.map((r) => (
                    <div key={r.id} className="flex items-center gap-4 bg-white dark:bg-boxdark p-4 rounded shadow-sm">
                      <div className="text-primario-celeste-800">
                        <FaFileAlt size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="text-title-sm font-semibold text-gray-800">{r.descripcion}</div>
                        <div className="text-xs text-gray-500">{r.disponible ? 'Disponible' : 'No disponible'}</div>
                      </div>
                      <div className="text-right">
                        <div className="md:text-title-sm font-semibold text-primario-celeste-800">{toCurrency(r.total)}</div>
                        <button onClick={() => verRecibo(r)} className="mt-2 text-sm text-primario-celeste-800 hover:underline">Ver detalle</button>
                      </div>
                    </div>
                  ))}
                </div>
                          )}

                <div className="flex gap-3 justify-center mt-3">                  
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
                    >
                        Volver
                    </button>
                </div>
            </>
          )}
        </div>
      </div>
    </DefaultLayout>
  );
};

export default ReciboDeHaberes;